import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { useAppSelector } from "../store";

interface ICurrency {
  value: string;
  image: string;
}

type Step = "ViewDetails" | "AddDetails" | "Summary" | "Complete";

export interface IBeneficiaryState {
  currency: ICurrency;
  beneficiaryName: string;
  isBeneficiaryAddressEntered: boolean;
  beneficiaryAddressSearch: string;
  beneficiaryAddress: {
    streetAddress: string;
    city: string;
    state: string;
    country: string;
    postcode: string;
    unit: string;
  };
  bankName: string;
  isBankAddressEntered: boolean;
  bankAddressSearch: string;
  bankAddress: {
    streetAddress: string;
    city: string;
    state: string;
    country: string;
    postcode: string;
    unit: string;
  };
  swiftCode: string;
  // We combine IBAN and Account Numbers to keep aligned with the NetSuite;
  // NetSuite stores them under the same field, and we use the final four digits
  // of either/or for display purposes.
  ibanAccNumber: string;
  transitNumber?: string;
  institutionNumber?: string;
  abaRoutingNumber?: string;
  sortCodeOrBsb?: string;
  identityType: "Individual" | "Business";
  accountType: "SVGS" | "CACC";
  error: string;
  step: "ViewDetails" | "AddDetails" | "Summary" | "Complete";
}

export const initialState: IBeneficiaryState = {
  currency: {
    value: "AUD",
    image: "https://flagicons.lipis.dev/flags/4x3/au.svg",
  },
  beneficiaryName: "",
  isBeneficiaryAddressEntered: false,
  beneficiaryAddressSearch: "",
  beneficiaryAddress: {
    streetAddress: "",
    city: "",
    state: "",
    country: "",
    postcode: "",
    unit: "",
  },
  bankName: "",
  isBankAddressEntered: false,
  bankAddressSearch: "",
  bankAddress: {
    streetAddress: "",
    city: "",
    state: "",
    country: "",
    postcode: "",
    unit: "",
  },
  swiftCode: "",
  ibanAccNumber: "",
  transitNumber: "",
  institutionNumber: "",
  abaRoutingNumber: "",
  sortCodeOrBsb: "",
  identityType: "Individual",
  accountType: "CACC",
  error: "",
  step: "ViewDetails",
};

export const resetNonAddressFieldsThunk = createAsyncThunk(
  "beneficiary/resetNonAddressFieldsThunk",
  async (_, { dispatch }) => {
    dispatch(resetNonAddressFields());
  },
);

const BeneficiarySlice = createSlice({
  name: "beneficiary",
  initialState: initialState,
  reducers: {
    setBeneficiaryState: (state, { payload }: PayloadAction<IBeneficiaryState>) => {
      return { ...state, ...payload };
    },
    setCurrency: (state, { payload }: PayloadAction<ICurrency>) => {
      state.currency = payload;
    },
    setBeneficiaryName: (state, { payload }: PayloadAction<string>) => {
      state.beneficiaryName = payload;
    },
    setBeneficiaryAddressSearch: (state, { payload }: PayloadAction<string>) => {
      state.beneficiaryAddressSearch = payload;
    },
    setIsBeneficiaryAddressEntered: (state, { payload }: PayloadAction<boolean>) => {
      state.isBeneficiaryAddressEntered = payload;
    },
    setBeneficiaryAddress: (
      state,
      { payload }: PayloadAction<IBeneficiaryState["beneficiaryAddress"]>,
    ) => {
      const newAddress = {
        streetAddress: payload.streetAddress || "",
        city: payload.city || "",
        state: payload.state || "",
        country: payload.country || "",
        postcode: payload.postcode || "",
        unit: payload.unit || "",
      };

      state.beneficiaryAddress = newAddress;
    },
    updateBeneficiaryAddressField: (
      state,
      {
        payload,
      }: PayloadAction<{ name: keyof IBeneficiaryState["beneficiaryAddress"]; value: string }>,
    ) => {
      state.beneficiaryAddress[payload.name] = payload.value;
    },
    setBankName: (state, { payload }) => {
      state.bankName = payload;
    },
    setBankAddressSearch: (state, { payload }: PayloadAction<string>) => {
      state.bankAddressSearch = payload;
    },
    setIsBankAddressEntered: (state, { payload }: PayloadAction<boolean>) => {
      state.isBankAddressEntered = payload;
    },
    setBankAddress: (state, { payload }: PayloadAction<IBeneficiaryState["bankAddress"]>) => {
      const newAddress = {
        streetAddress: payload.streetAddress || "",
        city: payload.city || "",
        state: payload.state || "",
        country: payload.country || "",
        postcode: payload.postcode || "",
        unit: payload.unit || "",
      };

      state.bankAddress = newAddress;
    },
    updateBankAddressField: (
      state,
      { payload }: PayloadAction<{ name: keyof IBeneficiaryState["bankAddress"]; value: string }>,
    ) => {
      state.bankAddress[payload.name] = payload.value;
    },
    setSwiftCode: (state, { payload }: PayloadAction<string>) => {
      state.swiftCode = payload;
    },
    setIbanAccNumber: (state, { payload }: PayloadAction<string>) => {
      state.ibanAccNumber = payload;
    },
    setIntitutionNumber: (state, { payload }: PayloadAction<string>) => {
      state.institutionNumber = payload;
    },
    setTransitNumber: (state, { payload }: PayloadAction<string>) => {
      state.transitNumber = payload;
    },
    setAbaRoutingNumberNumber: (state, { payload }: PayloadAction<string>) => {
      state.abaRoutingNumber = payload;
    },
    setSortCodeOrBsb: (state, { payload }: PayloadAction<string>) => {
      state.sortCodeOrBsb = payload;
    },
    setIdentityType: (state, { payload }: PayloadAction<"Individual" | "Business">) => {
      state.identityType = payload;
    },
    setAccountType: (state, { payload }: PayloadAction<"SVGS" | "CACC">) => {
      state.accountType = payload;
    },
    setError: (state, { payload }: PayloadAction<string>) => {
      state.error = payload;
    },
    setStep: (state, { payload }: PayloadAction<Step>) => {
      state.step = payload;
    },
    resetNonAddressFields: (state: IBeneficiaryState) => {
      state.abaRoutingNumber = "";
      state.institutionNumber = "";
      state.transitNumber = "";
      state.abaRoutingNumber = "";
      state.sortCodeOrBsb = "";
      state.ibanAccNumber = "";
      state.swiftCode = "";
    },
  },
});

export const {
  setBeneficiaryState,
  setBeneficiaryName,
  setBeneficiaryAddressSearch,
  setIsBeneficiaryAddressEntered,
  setBeneficiaryAddress,
  updateBeneficiaryAddressField,
  setBankName,
  setSwiftCode,
  setBankAddressSearch,
  setIsBankAddressEntered,
  setBankAddress,
  setCurrency,
  setIbanAccNumber,
  updateBankAddressField,
  setIntitutionNumber,
  setTransitNumber,
  setAbaRoutingNumberNumber,
  setSortCodeOrBsb,
  setError,
  setStep,
  resetNonAddressFields,
  setIdentityType,
  setAccountType,
} = BeneficiarySlice.actions;

export const useBeneficiarySlice = () => useAppSelector((state) => state.beneficiary);

export { BeneficiarySlice };
